import { getFallbackBallPosition } from "@/pages/Tagging/utils/getFallbackBallPosition";
import { nudgeBallPosition } from "@/pages/Tagging/utils/nudgeBallPosition";

import { OUTS_PER_INNING } from "../constants/game";
import {
  endsAtBat,
  isContactTypeAllowed,
  PITCH_TYPE_OPTIONS,
} from "../constants/tagOptions";
import {
  Activity,
  GameBaseRunners,
  GameFullGame,
  GameFullResponse,
  HitData,
  PitchData,
  PlateAppearance,
  ResultingState,
  Sport,
} from "../types/gameFull";
import { doesTagUpdateGameState } from "./plateAppearanceFeed";

export type BaserunnerFlags = {
  first: boolean;
  second: boolean;
  third: boolean;
};

export type PitchTags = {
  pitchType: string | null;
  pitchResult: string | null;
  playResult: string | null;
  contactType: string | null;
  runsScored: number;
  outsRecorded: number;
  baseRunners: BaserunnerFlags;
};

const BASES = ["first", "second", "third"] as const;

// TODO: Replace with real runner identity once base runner editing tracks players
const UNKNOWN_RUNNER = { playerId: "unknown", playerName: "Runner" };

export const toBaserunnerFlags = (
  baseRunners: GameBaseRunners,
): BaserunnerFlags => ({
  first: Boolean(baseRunners.first),
  second: Boolean(baseRunners.second),
  third: Boolean(baseRunners.third),
});

/**
 * Game state going into this pitch: the live state for the next pitch to tag,
 * else the latest earlier pitch that has a resulting state. A new at bat
 * starts 0-0; a new half-inning also clears outs and bases. Score always
 * carries over.
 */
export const getStateBeforePitch = (
  data: GameFullResponse,
  activity: Activity,
): ResultingState | null => {
  const pa = data.plateAppearances.find(
    (p) => p.plateAppearanceId === activity.plateAppearanceId,
  );
  if (!pa) return activity.resultingState ?? null;

  return {
    inning: pa.inning,
    isTopOfInning: pa.isTopOfInning,
    ...(!activity.result && doesTagUpdateGameState(data, activity.activityId)
      ? getLiveSituation(data.game)
      : getCarriedSituation(data, activity, pa)),
    currentHitter: pa.hitter,
    currentPitcher: pa.pitcher,
    plateAppearancesInHalfInning: 0,
    outsRecorded: 0,
    runsScored: 0,
  };
};

type Situation = Pick<
  ResultingState,
  "outs" | "balls" | "strikes" | "score" | "baseRunners"
>;

/**
 * The live state is what the next pitch to tag is thrown into, including
 * edits from the Edit Live Game State modal. Earlier pitches' resulting
 * states never see those edits.
 */
const getLiveSituation = (game: GameFullGame): Situation => {
  // A third out saved locally stands until the game service ends the half
  const isHalfOver = game.outs >= OUTS_PER_INNING;
  return {
    outs: isHalfOver ? 0 : game.outs,
    balls: game.balls,
    strikes: game.strikes,
    score: game.score,
    baseRunners: isHalfOver ? {} : game.baseRunners,
  };
};

/** Carried over from the latest earlier pitch that has a resulting state. */
const getCarriedSituation = (
  data: GameFullResponse,
  activity: Activity,
  pa: PlateAppearance,
): Situation => {
  const previousActivity = data.activities
    .filter(
      (a) => a.resultingState && a.pitch.eventTime < activity.pitch.eventTime,
    )
    .sort((a, b) => b.pitch.eventTime - a.pitch.eventTime)[0];
  const previous = previousActivity?.resultingState;

  const isSamePA =
    previousActivity?.plateAppearanceId === activity.plateAppearanceId;
  const isSameHalf =
    previous?.inning === pa.inning &&
    previous.isTopOfInning === pa.isTopOfInning;

  return {
    outs: isSameHalf ? previous.outs : 0,
    balls: isSamePA && previous ? previous.balls : 0,
    strikes: isSamePA && previous ? previous.strikes : 0,
    score: previous?.score ?? { home: 0, away: 0 },
    baseRunners: isSameHalf ? previous.baseRunners : {},
  };
};

/** Empty tag form for the waiting row; runners default to the live state. */
export const getEmptyTags = (baseRunners: GameBaseRunners): PitchTags => ({
  pitchType: null,
  pitchResult: null,
  playResult: null,
  contactType: null,
  runsScored: 0,
  outsRecorded: 0,
  baseRunners: toBaserunnerFlags(baseRunners),
});

/** Tag form values pre-filled from an activity's existing tags and state. */
export const getInitialTags = (
  data: GameFullResponse,
  activity: Activity,
): PitchTags => {
  const { result, resultingState } = activity;
  const stateBefore = getStateBeforePitch(data, activity);
  const suggestedPitchResult = getSuggestedPitchResult(activity.pitch.hitData);

  return {
    // Untagged pitches start on the tracker's suggestions
    pitchType: result
      ? (result.pitchType ?? null)
      : getSuggestedPitchType(activity.pitch.pitchData, data.game.sport),
    pitchResult: result ? (result.pitchResult ?? null) : suggestedPitchResult,
    // An HBP's play result isn't sent, so a saved one comes back without it
    playResult:
      result?.playResult ??
      (result?.pitchResult === "HitByPitch" ? "HitByPitch" : null),
    contactType: result
      ? (result.contactType ?? null)
      : getContactTypeFor(suggestedPitchResult, activity.pitch.hitData),
    runsScored: resultingState?.runsScored ?? 0,
    outsRecorded: resultingState?.outsRecorded ?? 0,
    baseRunners: toBaserunnerFlags(
      resultingState?.baseRunners ?? stateBefore?.baseRunners ?? {},
    ),
  };
};

export const buildBaseRunners = (
  flags: BaserunnerFlags,
  existing: GameBaseRunners,
): GameBaseRunners =>
  BASES.reduce<GameBaseRunners>((acc, base) => {
    if (flags[base]) acc[base] = existing[base] ?? UNKNOWN_RUNNER;
    return acc;
  }, {});

/**
 * Count after a pitch. When the at bat ended the next hitter starts 0-0.
 * Fouls only add a strike before two strikes.
 */
const getCountAfterPitch = (
  before: { balls: number; strikes: number },
  pitchResult: string | null,
  playResult: string | null,
) => {
  if (endsAtBat(pitchResult, playResult)) return { balls: 0, strikes: 0 };

  switch (pitchResult) {
    case "Ball":
      return { ...before, balls: before.balls + 1 };
    case "CalledStrike":
    case "SwingingStrike":
      return { ...before, strikes: before.strikes + 1 };
    case "Foul":
      return { ...before, strikes: Math.min(before.strikes + 1, 2) };
    default:
      return before;
  }
};

/**
 * Applies locally saved tags over the game data. The saved pitch's resulting
 * state is rebuilt, and when it is the latest pitch of the active at bat the
 * live game state follows it. Later pitches are not recalculated (the backend
 * will own that once the save API exists).
 */
export const applyTagOverrides = (
  data: GameFullResponse,
  overrides: Record<string, PitchTags>,
): GameFullResponse => {
  if (Object.keys(overrides).length === 0) return data;

  const activities = data.activities.map((activity) => {
    const tags = overrides[activity.activityId];
    if (!tags) return activity;

    const stateBefore =
      getStateBeforePitch(data, activity) ?? activity.resultingState;
    const resultingState: ResultingState | undefined = stateBefore && {
      ...stateBefore,
      ...activity.resultingState,
      outs: stateBefore.outs + tags.outsRecorded,
      score: {
        away:
          stateBefore.score.away +
          (stateBefore.isTopOfInning ? tags.runsScored : 0),
        home:
          stateBefore.score.home +
          (stateBefore.isTopOfInning ? 0 : tags.runsScored),
      },
      baseRunners: buildBaseRunners(
        tags.baseRunners,
        activity.resultingState?.baseRunners ?? stateBefore.baseRunners,
      ),
      ...getCountAfterPitch(stateBefore, tags.pitchResult, tags.playResult),
      outsRecorded: tags.outsRecorded,
      runsScored: tags.runsScored,
    };

    return {
      ...activity,
      tagCount: Math.max(activity.tagCount, 1),
      result: {
        pitcherSet: null,
        outcome: null,
        ...activity.result,
        pitchType: tags.pitchType,
        pitchResult: tags.pitchResult,
        playResult: tags.playResult,
        contactType: tags.contactType,
        // The runners the tag reports (postTagOccupiedBases), on every pitch:
        // a runner can move on a ball or strike (a steal)
        occupiedBases: tags.baseRunners,
      },
      resultingState,
    };
  });

  // Saving a pitch that qualifies (spec 3.4) moves the live game state
  const latestState = activities
    .filter(
      (a) =>
        overrides[a.activityId] && doesTagUpdateGameState(data, a.activityId),
    )
    .sort((a, b) => b.pitch.eventTime - a.pitch.eventTime)[0]?.resultingState;

  return {
    ...data,
    activities,
    game: latestState
      ? {
          ...data.game,
          outs: latestState.outs,
          balls: latestState.balls,
          strikes: latestState.strikes,
          score: latestState.score,
          baseRunners: latestState.baseRunners,
        }
      : data.game,
  };
};

/** Values from the Edit Live Game State modal. */
export type GameStateEdit = {
  awayScore: number;
  homeScore: number;
  baserunners: BaserunnerFlags;
  inning: number;
  isTopInning: boolean;
  balls: number;
  strikes: number;
  outs: number;
};

/** Applies a manual game state edit over the live game state. */
export const applyGameStateEdit = (
  data: GameFullResponse,
  edit: GameStateEdit | null,
): GameFullResponse =>
  edit
    ? {
        ...data,
        game: {
          ...data.game,
          inning: edit.inning,
          isTopOfInning: edit.isTopInning,
          outs: edit.outs,
          balls: edit.balls,
          strikes: edit.strikes,
          score: { away: edit.awayScore, home: edit.homeScore },
          baseRunners: buildBaseRunners(
            edit.baserunners,
            data.game.baseRunners,
          ),
        },
      }
    : data;

/**
 * Whether the tracker classifies the sport's pitches. Its classifier only
 * knows baseball pitches, so a softball pitch type is always the
 * scorekeeper's pick, even where the names overlap (Fastball, Curveball).
 */
export const isPitchTypeClassified = (sport: Sport) => sport === "baseball";

/** Classifier's pitch type, when it matches one of the sport's tag options. */
export const getSuggestedPitchType = (
  pitchData: PitchData | null,
  sport: Sport,
) => {
  if (!isPitchTypeClassified(sport)) return null;
  const type = pitchData?.AutoPitchClassification?.primaryPitch?.type;
  return PITCH_TYPE_OPTIONS[sport].some((option) => option.value === type)
    ? (type ?? null)
    : null;
};

/** Hit data means the ball was hit, so the tracker's fair/foul call decides. */
export const getSuggestedPitchResult = (hitData: HitData | null) => {
  switch (hitData?.AutoFairFoulCall) {
    case "fair":
      return "InPlay";
    case "foul":
      return "Foul";
    default:
      return null;
  }
};

/**
 * Contact type from launch angle, using Statcast's buckets with each lower
 * bound inclusive. Bunts aren't detectable from the angle, so never suggested.
 */
export const getSuggestedContactType = (hitData: HitData | null) => {
  const angle = hitData?.AngleDegrees;
  if (typeof angle !== "number" || !Number.isFinite(angle)) return null;
  if (angle < 10) return "GroundBall";
  if (angle < 25) return "LineDrive";
  if (angle < 50) return "FlyBall";
  return "Popup";
};

/** The suggested contact type, if the pitch result allows one. */
export const getContactTypeFor = (
  pitchResult: string | null,
  hitData: HitData | null,
) =>
  isContactTypeAllowed(pitchResult) ? getSuggestedContactType(hitData) : null;

/**
 * Ball position on the strike zone in 0-100 percentage space, mirroring the
 * V1 StrikeZone logic: relative coordinates when present, else the legacy
 * feet-based fallback.
 */
export const getStrikeZoneBall = (
  pitchData: PitchData | null,
  perspective: "pitcher" | "catcher",
  isSoftball = false,
) => {
  if (!pitchData) return null;

  const {
    PlateLocSideSZRelative,
    PlateLocHeightSZRelative,
    PlateLocSideFeet,
    PlateLocHeightFeet,
  } = pitchData;

  if (
    typeof PlateLocSideSZRelative === "number" &&
    typeof PlateLocHeightSZRelative === "number"
  ) {
    const isStrike = pitchData.AutoPitchCall === "strike";
    const nudged = nudgeBallPosition({
      plateX: PlateLocSideSZRelative,
      plateY: PlateLocHeightSZRelative,
      isStrike,
      isSoftball,
    });
    return {
      ballX: nudged.plateX * (perspective === "pitcher" ? 50 : -50) + 50,
      ballY: 100 - nudged.plateY * 100,
      isStrike,
    };
  }

  if (
    typeof PlateLocSideFeet === "number" &&
    typeof PlateLocHeightFeet === "number"
  ) {
    return (
      getFallbackBallPosition({
        plateLocSideFeet: PlateLocSideFeet,
        plateLocHeightFeet: PlateLocHeightFeet,
        zoneAccuracy: pitchData.ZoneAccuracy,
        perspective,
        isSoftball,
      }) ?? null
    );
  }

  return null;
};
